import { Link } from "@/components/link";
import { useLocation } from "@tanstack/react-router";

import * as React from "react";
import { ChevronsUpDown, Search } from "lucide-react";

import { navItems } from "@/lib/dashboard-data";
import { navIconMap } from "@/components/nav-icons";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { VoiceNoteButton } from "@/components/voice-note";
import { CommandPalette } from "@/components/command-palette";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";

export function SiteHeader() {
  const pathname = useLocation({ select: (s) => s.pathname });
  const current =
    navItems.find((p) => p.href === pathname) ??
    navItems.find((p) => p.href !== "/" && pathname.startsWith(`${p.href}/`)) ??
    navItems[0];
  const [searchOpen, setSearchOpen] = React.useState(false);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    const onOpenSearch = () => setSearchOpen(true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("dashboard:open-search", onOpenSearch);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("dashboard:open-search", onOpenSearch);
    };
  }, []);

  return (
    <header className="@container/header flex h-12 shrink-0 items-center gap-1 border-b bg-background px-2 pt-[env(safe-area-inset-top)] box-content sm:h-14 sm:gap-2 sm:px-4 [@media(max-height:480px)]:h-11">
      <SidebarTrigger className="-ml-1" />
      <Separator
        orientation="vertical"
        className="hidden data-[orientation=vertical]:h-6 data-[orientation=vertical]:self-center sm:block"
      />

      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="ghost" className="max-w-32 truncate px-2 sm:max-w-none sm:px-2.5" />}>
          <span className="truncate">{current.title}</span>
          <ChevronsUpDown data-icon="inline-end" className="shrink-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-44">
          {navItems.map((page) => {
            const Icon = navIconMap[page.icon];
            return (
              <DropdownMenuItem
                key={page.title}
                render={<Link href={page.href} />}
              >
                <Icon />
                {page.title}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Saved-views slot: pages render <SavedViews> into this via a portal. */}
      <div id="header-views" className="flex min-w-0 flex-1 items-center overflow-hidden" />

      <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
        <span className="hidden sm:inline-flex">
          <VoiceNoteButton />
        </span>
        <span className="hidden sm:inline-flex">
          <ThemeToggle />
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Search (Ctrl or Cmd + K)"
          onClick={() => setSearchOpen(true)}
        >
          <Search />
        </Button>
      </div>

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
